import { useEffect, useRef, useState } from 'react';
import { LayoutGrid, Loader2, Plus, SearchX, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { trpc } from '@/lib/trpc';
import { OrderProductCatalog } from './order-product-catalog';
import { OrderProductIdentity, type OrderProduct } from './order-product-result';
export type { OrderProduct } from './order-product-result';

interface OrderProductPickerProps {
  items: { productId?: string; quantity: number }[];
  onAdd: (product: OrderProduct) => Promise<void>;
}

export function OrderProductPicker({ items, onAdd }: OrderProductPickerProps) {
  const { t } = useTranslation('trading');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const addingRef = useRef(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  const { data, isFetching, isError, refetch } = trpc.tradingProduct.list.useQuery(
    { search: debouncedSearch || undefined, limit: 12, includeInactive: false },
    { enabled: open, staleTime: 30_000 }
  );
  const products = data?.products ?? [];
  const loading = isFetching || search.trim() !== debouncedSearch;
  const openCatalog = () => {
    setOpen(false);
    setCatalogOpen(true);
  };

  const addProduct = async (product: OrderProduct) => {
    if (addingRef.current) return;
    addingRef.current = true;
    setAddingId(product.id);
    setAnnouncement('');
    try {
      await onAdd(product);
      setAnnouncement(t('sales.orders.productAdded', { name: product.name }));
    } finally {
      addingRef.current = false;
      setAddingId(null);
    }
  };

  return (
    <div className="min-w-0 flex-1">
      <div className="flex flex-col gap-2 sm:flex-row">
        <Popover open={open} onOpenChange={setOpen}>
          <Command
            label={t('sales.orders.addProduct')}
            shouldFilter={false}
            className="border-input bg-background focus-within:border-foreground focus-within:ring-foreground/20 relative min-w-0 flex-1 overflow-visible border focus-within:ring-1 [&_[cmdk-input-wrapper]]:border-0"
            onBlur={(event) => {
              if (
                !event.currentTarget.contains(event.relatedTarget) &&
                !contentRef.current?.contains(event.relatedTarget)
              )
                setOpen(false);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                setOpen(false);
              }
              if (event.key === 'Enter' && !open) event.preventDefault();
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') setOpen(true);
            }}
          >
            <PopoverAnchor asChild>
              <div ref={anchorRef} className="relative">
                <CommandInput
                  asChild
                  ref={inputRef}
                  value={search}
                  onValueChange={(value) => {
                    setSearch(value);
                    setOpen(true);
                  }}
                  onFocus={() => setOpen(true)}
                  onClick={() => setOpen(true)}
                  placeholder={t('sales.orders.productSearchPlaceholder')}
                  aria-label={t('sales.orders.addProduct')}
                  className="h-11 pr-8"
                >
                  <input aria-expanded={open} />
                </CommandInput>
                {search && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1.5"
                    aria-label={t('sales.orders.clearSearch')}
                    onKeyDown={(event) => event.stopPropagation()}
                    onClick={() => {
                      setSearch('');
                      inputRef.current?.focus();
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </PopoverAnchor>
            <PopoverContent
              ref={contentRef}
              align="start"
              sideOffset={6}
              collisionPadding={12}
              className="flex max-h-[var(--radix-popover-content-available-height)] w-[var(--radix-popover-trigger-width)] flex-col p-0 shadow-lg"
              onOpenAutoFocus={(event) => event.preventDefault()}
              onCloseAutoFocus={(event) => event.preventDefault()}
              onInteractOutside={(event) => {
                if (event.target instanceof Node && anchorRef.current?.contains(event.target))
                  event.preventDefault();
              }}
            >
              <div className="text-muted-foreground border-b px-3 py-2 text-xs font-medium">
                {t(debouncedSearch ? 'sales.orders.searchResults' : 'sales.orders.browseProducts')}
              </div>
              <CommandList
                label={t('sales.orders.addProduct')}
                aria-busy={loading}
                className="min-h-0"
              >
                {loading ? (
                  <div
                    role="status"
                    className="text-muted-foreground flex items-center gap-2 p-4 text-sm"
                  >
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('sales.orders.loadingProducts')}
                  </div>
                ) : isError ? (
                  <div role="alert" className="space-y-2 p-4 text-sm">
                    <p>{t('sales.orders.productSearchError')}</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onKeyDown={(event) => event.stopPropagation()}
                      onClick={() => void refetch()}
                    >
                      {t('sales.orders.retrySearch')}
                    </Button>
                  </div>
                ) : products.length === 0 ? (
                  <div className="flex items-start gap-3 p-4 text-sm">
                    <SearchX className="text-muted-foreground mt-0.5 h-5 w-5 shrink-0" />
                    <div>
                      <p className="font-medium">{t('products.noProducts')}</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        {t('sales.orders.noProductsHint')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <CommandGroup>
                    {products.map((product) => {
                      const quantity = items.find(
                        (item) => item.productId === product.id
                      )?.quantity;
                      return (
                        <CommandItem
                          key={product.id}
                          value={product.id}
                          disabled={addingId !== null}
                          onSelect={() => void addProduct(product)}
                          onMouseDown={(event) => event.preventDefault()}
                          className="cursor-pointer gap-3 px-3 py-3"
                        >
                          <OrderProductIdentity product={product} quantity={quantity} />
                          <span className="flex shrink-0 items-center gap-1.5 text-xs font-medium">
                            {addingId === product.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Plus className="h-4 w-4" />
                            )}
                            {t(
                              quantity ? 'sales.orders.addOneMore' : 'sales.orders.addProductShort'
                            )}
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                )}
              </CommandList>
              <div className="bg-muted/30 shrink-0 border-t p-2">
                <Button
                  type="button"
                  variant="ghost"
                  className="h-auto min-h-9 w-full gap-2 whitespace-normal text-xs"
                  onKeyDown={(event) => event.stopPropagation()}
                  onClick={openCatalog}
                >
                  <LayoutGrid className="h-4 w-4 shrink-0" />
                  {data && data.pagination.total > products.length
                    ? t('sales.orders.viewAllResults', { count: data.pagination.total })
                    : t('sales.orders.browseByBrand')}
                </Button>
              </div>
            </PopoverContent>
          </Command>
        </Popover>
        <Button
          type="button"
          variant="outline"
          className="h-[46px] shrink-0 gap-2 px-4"
          onClick={openCatalog}
        >
          <LayoutGrid className="h-4 w-4" />
          {t('sales.orders.openCatalog')}
        </Button>
      </div>
      {catalogOpen && (
        <OrderProductCatalog
          open={catalogOpen}
          onOpenChange={setCatalogOpen}
          initialSearch={search}
          items={items}
          addingId={addingId}
          announcement={announcement}
          onAdd={addProduct}
        />
      )}
      <span role="status" className="sr-only">
        {announcement}
      </span>
    </div>
  );
}
